import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { useAuth } from '@/shared/AuthContext';

interface AdminUser {
  id: string;
  email: string;
  nickname: string;
  approved: boolean;
  isOperative: boolean;
  homeTenantId: number;
  roles: string[];
}

interface AdminTenant {
  id: number;
  name: string;
  slug?: string;
  enabled?: boolean;
}

const ALL_ROLES = ['SuperAdmin', 'Amministratore', 'Magazzino', 'TecnicoIdranti', 'RisorseUmane', 'Utente'] as const;
const OPERATIONAL_ROLES = ['Utente', 'Magazzino', 'TecnicoIdranti', 'RisorseUmane', 'Amministratore'];

// Un utente con SOLO ruoli amministrativi (es. SuperAdmin senza Utente) e' un
// "utente di sistema" — non un pompiere — quindi il suo distaccamento di
// appartenenza e' solo identificativo, non funzionale.
function isSystemUser(user: { roles: string[] }): boolean {
  return user.roles.length > 0 && !user.roles.some(r => OPERATIONAL_ROLES.includes(r));
}

export function AdminPage(): React.JSX.Element {
  usePageTitle('Admin');
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { user, hasRole } = useAuth();
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  // Mai utenti di distaccamenti diversi mescolati: il server scopa SEMPRE al
  // tenant del chiamante; il SOLO SuperAdmin puo' cambiare distaccamento con
  // la tendina esplicita (un tenant alla volta, niente "Tutti").
  const isSuperAdmin = hasRole('SuperAdmin');
  // Deep-link dalla notifica "nuova richiesta" (/admin?tenant=ID): il SuperAdmin
  // atterra direttamente sul distaccamento giusto. Per il non-SuperAdmin il
  // param e' ignorato (vede comunque solo il proprio). Default: il proprio tenant.
  const [searchParams] = useSearchParams();
  const deepLinkTenant = isSuperAdmin
    ? Number(searchParams.get('tenant')) || undefined
    : undefined;
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(
    deepLinkTenant ?? user?.homeTenantId);
  // Per il non-SuperAdmin il param non viene inviato (e il server lo
  // ignorerebbe comunque): comportamento attuale invariato.
  const tenantParams = isSuperAdmin && selectedTenantId !== undefined
    ? { tenantId: selectedTenantId } : undefined;
  const tenantKey = tenantParams?.tenantId ?? null;

  const { data: pending, refetch: refetchPending } = useQuery<AdminUser[]>({
    queryKey: ['admin.users.pending', tenantKey],
    queryFn: async () => (await api.get('/admin/users/pending', { params: tenantParams })).data,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });
  const { data: users, refetch: refetchUsers } = useQuery<AdminUser[]>({
    queryKey: ['admin.users', tenantKey],
    queryFn: async () => (await api.get('/admin/users', { params: tenantParams })).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  // Usiamo /tenants/public (accessibile a tutti gli autenticati) invece di
  // /admin/tenants (richiede SuperAdmin) — basta per la lookup nome distaccamento.
  const { data: tenants } = useQuery<AdminTenant[]>({
    queryKey: ['tenants.public'],
    queryFn: async () => (await api.get('/tenants/public')).data,
  });
  // Tendina SuperAdmin: lista completa distaccamenti (endpoint SuperAdmin-only,
  // include anche quelli disabilitati). Fetch solo se il ruolo lo consente.
  const { data: adminTenants } = useQuery<AdminTenant[]>({
    queryKey: ['admin.tenants'],
    queryFn: async () => (await api.get('/admin/tenants')).data,
    enabled: isSuperAdmin,
  });

  const saveUser = useMutation({
    mutationFn: async (user: AdminUser) => {
      await api.put(`/admin/users/${user.id}`, {
        nickname: user.nickname,
        approved: user.approved,
        isOperative: user.isOperative,
        homeTenantId: user.homeTenantId,
        roles: user.roles && user.roles.length ? user.roles : ['Utente'],
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.users'] });
      qc.invalidateQueries({ queryKey: ['admin.users.pending'] });
      toast.show('Utente aggiornato');
      setEditingUser(null);
    },
    onError: () => toast.show('Errore salvataggio', 'error'),
  });

  // Lookup nome: il SuperAdmin usa la lista completa (/admin/tenants, include i
  // disabilitati), gli altri la lista pubblica (solo distaccamenti abilitati).
  const tenantList = (isSuperAdmin ? adminTenants : tenants) ?? [];
  const tenantName = (id: number) => tenantList.find(t => t.id === id)?.name ?? `#${id}`;
  const tenantLabel = (u: AdminUser) =>
    isSystemUser(u) ? 'Sistema' : tenantName(u.homeTenantId);

  const navigate = useNavigate();

  async function handleDelete(u: AdminUser) {
    const ok = await confirm({
      title: 'Elimina utente',
      message: `Eliminare ${u.nickname} (${u.email})? Operazione irreversibile.`,
      confirmLabel: 'Elimina', variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      qc.invalidateQueries({ queryKey: ['admin.users'] });
      qc.invalidateQueries({ queryKey: ['admin.users.pending'] });
      toast.show('Utente eliminato');
    } catch {
      toast.show('Errore eliminazione', 'error');
    }
  }

  function toggleOperative(u: AdminUser, isOperative: boolean) {
    saveUser.mutate({ ...u, isOperative });
  }

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/dashboard')}
        style={{ marginBottom: 20 }}>← Indietro</button>

      <h3 style={{ textAlign: 'center', fontSize: 28, marginBottom: 20 }}>👥 Gestione Utenti</h3>

      {/* Solo SuperAdmin: selettore esplicito del distaccamento da gestire
          (un tenant alla volta — mai liste mescolate). */}
      {isSuperAdmin && (
        <div className="form-group" style={{ maxWidth: 420, margin: '0 auto 16px' }}>
          <label htmlFor="admin-tenant-filter">Distaccamento</label>
          <select id="admin-tenant-filter" value={selectedTenantId ?? ''}
            onChange={e => setSelectedTenantId(Number(e.target.value))}>
            {(adminTenants ?? []).map(t => (
              <option key={t.id} value={t.id}>
                {t.name}{t.enabled === false ? ' (disabilitato)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }}
          onClick={() => { refetchPending(); refetchUsers(); }}>
          🔄 Aggiorna
        </button>
      </div>

      {/* Utenti pending */}
      {pending?.map(u => (
        <div key={u.id} className="user-item pending">
          <strong>{u.nickname}</strong>
          <span className="status-badge">IN ATTESA</span>
          <br />
          <small>{u.email}</small>
          <br />
          <small style={{ color: '#666' }}>📍 {tenantLabel(u)}</small>
          <div className="button-group">
            <button className="btn-accept"
              onClick={() => saveUser.mutate({ ...u, approved: true, isOperative: true, roles: ['Utente'] })}>
              ✓ Accetta
            </button>
            <button className="btn-edit-mini"
              onClick={() => setEditingUser({ ...u, approved: true, isOperative: true })}>
              ✎ Accetta con ruoli
            </button>
            <button className="btn-delete-mini"
              onClick={() => handleDelete(u)}>
              🗑️ Elimina
            </button>
          </div>
        </div>
      ))}

      {/* Utenti approvati */}
      {users?.filter(u => u.approved).map(u => {
        const isAdmin = u.roles.includes('SuperAdmin') || u.roles.includes('Amministratore');
        const isSystem = isSystemUser(u);
        return (
          <div key={u.id} className={`user-item ${isAdmin ? 'admin' : ''}`}>
            <strong>{u.nickname}</strong>
            {isSystem && <span className="system-badge">SISTEMA</span>}
            <br />
            <small>{u.email}</small>
            <br />
            <small style={{ color: '#666' }}>📍 {tenantLabel(u)}</small>

            <div className="role-badges">
              {u.roles.map(r => <span key={r} className="role-badge">{r}</span>)}
            </div>

            <label className="checkbox-label">
              <input type="checkbox" checked={u.isOperative}
                onChange={(e) => toggleOperative(u, e.target.checked)} />
              <span>Operativo (riceve selettive)</span>
            </label>

            <div className="button-group">
              <button className="btn-edit-mini" onClick={() => setEditingUser(u)}>
                ✎ Modifica
              </button>
              <button className="btn-delete-mini" onClick={() => handleDelete(u)}>
                🗑️ Elimina Utente
              </button>
            </div>
          </div>
        );
      })}

      {(!users || users.length === 0) && (
        <div style={{ textAlign: 'center', color: '#6c757d', fontSize: 16, padding: 40 }}>
          Nessun utente registrato
        </div>
      )}

      {editingUser && (
        <UserEditModal
          user={editingUser}
          tenants={tenantList}
          onClose={() => setEditingUser(null)}
          onSave={(u) => saveUser.mutate(u)}
          onDelete={() => {
            setEditingUser(null);
            handleDelete(editingUser);
          }}
        />
      )}
    </div>
  );
}

function UserEditModal({ user, tenants, onClose, onSave, onDelete }: {
  user: AdminUser;
  tenants: AdminTenant[];
  onClose: () => void;
  onSave: (u: AdminUser) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const [nickname, setNickname] = useState(user.nickname);
  const [approved, setApproved] = useState(user.approved);
  const [isOperative, setIsOperative] = useState(user.isOperative);
  const [homeTenantId, setHomeTenantId] = useState(user.homeTenantId);
  const [roles, setRoles] = useState<string[]>(user.roles ?? []);

  function toggleRole(r: string) {
    setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  }

  const systemUser = roles.length > 0 && !roles.some(r => OPERATIONAL_ROLES.includes(r));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0 }}>Modifica utente</h3>
            <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>{user.email}</div>
          </div>
          <button onClick={onClose} aria-label="Chiudi"
            style={{
              background: 'transparent', border: 'none', fontSize: 22, color: '#999',
              cursor: 'pointer', lineHeight: 1, padding: 4,
            }}>✕</button>
        </div>

        <div className="form-group">
          <label htmlFor="edit-nickname">Nickname</label>
          <input id="edit-nickname" type="text" value={nickname} onChange={e => setNickname(e.target.value)} />
        </div>


        <div className="form-group">
          <label htmlFor="edit-tenant">
            Distaccamento
            {systemUser && (
              <span style={{ fontWeight: 'normal', color: '#999', fontSize: 12, marginLeft: 8 }}>
                — solo identificativo (utente di sistema)
              </span>
            )}
          </label>
          <select id="edit-tenant" value={homeTenantId} onChange={e => setHomeTenantId(Number(e.target.value))}>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label style={{ marginBottom: 10 }}>Stato</label>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={approved}
                onChange={e => setApproved(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <span><strong>Approvato</strong> <span style={{ color: '#999', fontSize: 12 }}>(può loggarsi)</span></span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={isOperative}
                onChange={e => setIsOperative(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <span><strong>Operativo</strong> <span style={{ color: '#999', fontSize: 12 }}>(attivo nelle classifiche)</span></span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label style={{ marginBottom: 10 }}>Ruoli</label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 10,
            padding: 12,
            background: '#f7f8fa',
            borderRadius: 8,
            border: '1px solid #e4e6ea',
          }}>
            {ALL_ROLES.map(r => {
              const checked = roles.includes(r);
              return (
                <label key={r}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '6px 10px', borderRadius: 6, fontSize: 13,
                    background: checked ? '#e8efff' : 'transparent',
                    border: checked ? '1px solid #667eea' : '1px solid transparent',
                    transition: 'all 0.15s ease',
                  }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleRole(r)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontWeight: checked ? 600 : 400 }}>{r}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div style={{
          marginTop: 24, paddingTop: 18, borderTop: '1px solid #ececec',
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button className="btn-delete" onClick={onDelete} style={{ padding: '8px 14px' }}>
            🗑 Elimina
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-secondary" onClick={onClose} style={{ padding: '8px 18px' }}>
              Annulla
            </button>
            <button className="btn-success" onClick={() => onSave({
              ...user, nickname, approved, isOperative, homeTenantId, roles,
            })} style={{ padding: '8px 20px' }}>
              ✓ Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
